import React, { useEffect } from "react";
import {
  Container,
  Box,
  Paper,
  Typography,
  useTheme,
  useMediaQuery
} from "@mui/material";
import { logoLight, logoNoBackground, logoDark } from "assets";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "hooks/auth/useAuth";
import { useMFA } from "hooks/auth/useMFA";
import { MainLoading } from "@/components/loading/MainLoading";
import { motion } from "framer-motion";

const particleData = [
  { left: "20%", size: 121, delay: 1 },
  { left: "57%", size: 54, delay: 3 },
  { left: "78%", size: 74, delay: 6 },
  { left: "51%", size: 142, delay: 11 },
  { left: "7%", size: 135, delay: 5 },
  { left: "10%", size: 83, delay: 25 },
  { left: "11%", size: 35, delay: 17 },
  { left: "32%", size: 64, delay: 20 },
  { left: "81%", size: 146, delay: 11 },
  { left: "53%", size: 138, delay: 1 },
  { left: "68%", size: 88, delay: 46 },
  { left: "28%", size: 137, delay: 54 },
  { left: "70%", size: 100, delay: 7 },
  { left: "22%", size: 34, delay: 59 },
  { left: "78%", size: 59, delay: 5 },
  { left: "13%", size: 57, delay: 17 },
  { left: "27%", size: 85, delay: 45 },
  { left: "45%", size: 39, delay: 65 },
  { left: "46%", size: 17, delay: 67 },
  { left: "59%", size: 6, delay: 25 },
  { left: "72%", size: 85, delay: 57 },
  { left: "54%", size: 99, delay: 31 },
  { left: "73%", size: 136, delay: 76 },
  { left: "77%", size: 102, delay: 109 },
  { left: "10%", size: 1, delay: 52 },
  { left: "34%", size: 45, delay: 72 },
  { left: "62%", size: 122, delay: 24 },
  { left: "40%", size: 17, delay: 64 },
  { left: "67%", size: 7, delay: 37 },
  { left: "79%", size: 71, delay: 123 },
  { left: "17%", size: 121, delay: 63 },
  { left: "1%", size: 41, delay: 86 },
  { left: "23%", size: 126, delay: 112 },
  { left: "56%", size: 34, delay: 111 },
  { left: "81%", size: 26, delay: 91 },
  { left: "66%", size: 31, delay: 102 },
  { left: "60%", size: 95, delay: 10 },
  { left: "77%", size: 127, delay: 149 },
  { left: "16%", size: 46, delay: 74 },
  { left: "27%", size: 68, delay: 11 },
  { left: "50%", size: 44, delay: 100 },
  { left: "9%", size: 125, delay: 42 },
  { left: "23%", size: 92, delay: 200 },
  { left: "81%", size: 126, delay: 179 },
  { left: "72%", size: 90, delay: 102 },
  { left: "44%", size: 61, delay: 94 },
  { left: "86%", size: 140, delay: 37 },
  { left: "85%", size: 79, delay: 149 },
  { left: "10%", size: 101, delay: 93 },
  { left: "31%", size: 88, delay: 1 }
];

const AuthLayout: React.FC = () => {
  const { isLoading, currentUser, role } = useAuth();
  const { resolver } = useMFA();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (isLoading) return;
    
    if (role) {
      navigate(`/${role}`);
      return;
    } 
    
    if (resolver && location.pathname !== "/login-mfa-code") {
      navigate("/login-mfa-code");
      return;
    } 
    
    if (location.pathname === "/" && !currentUser) {
      navigate("/login");
      return;
    } 
  }, [resolver, location.pathname, navigate, currentUser, role, isLoading]);

  const isSignupPage = location.pathname === "/signup";

  if (isLoading) {
    return <MainLoading />;
  }

  if (role) {
    return <MainLoading />;
  }

  if (resolver && location.pathname !== "/login-mfa-code") {
    return <MainLoading />;
  }

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const logoVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut",
        delay: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.95 },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut",
        delay: 0.4
      }
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        background: "#000000",
      }}
    >
      <Box
        component="ul"
        sx={{
          position: "fixed",
          width: "100vw",
          height: "100vh",
          top: 0,
          left: 0,
          margin: 0,
          padding: 0,
          listStyle: "none",
          overflow: "hidden",
          zIndex: 0,
        }}
      >
                {particleData
          .filter((_, index) => !isMobile || index % 3 === 0)
          .map((particle, index) => (
          <Box
            key={index}
            component="li"
            sx={{
              position: "absolute",
              display: "block",
              listStyle: "none",
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              left: particle.left,
              bottom: `-${particle.size}px`,
              background: "rgba(255, 255, 255, 0.2)",
              animation: `floatUp 19s linear infinite`,
              animationDelay: `${particle.delay}s`,
              borderRadius: "2px",
              "@keyframes floatUp": {
                "0%": {
                  transform: "translateY(0) rotate(0deg)",
                  opacity: 1,
                  borderRadius: "0",
                },
                "100%": {
                  transform: "translateY(-1000px) rotate(720deg)",
                  opacity: 0,
                  borderRadius: "50%",
                }
              }
            }}
          />
        ))}
      </Box>

      <Container 
        component={motion.div}
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        maxWidth={isSignupPage ? "sm" : "xs"}
        sx={{ 
          position: "relative",
          zIndex: 1,
          py: 4
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 4
          }}
        >
          <motion.div variants={logoVariants}>
            <Box
              component="img"
              src={logoDark}
              alt="FreeTech Logo"
              sx={{
                width: isMobile ? "160px" : "160px",
                height: "auto",
                filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.3)) brightness(1.1)",
                borderRadius: "10px",
              }}
            />
          </motion.div>

          <motion.div variants={cardVariants} style={{ width: "100%" }}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, sm: 4 },
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                bgcolor: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(20px)",
                width: "100%",
                borderRadius: 3,
                border: `1px solid rgba(255, 255, 255, 0.2)`,
                boxShadow: `0 20px 40px -12px rgba(0,0,0,0.3), 
                           0 8px 16px -8px rgba(0,0,0,0.2)`,
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "4px",
                  background: `linear-gradient(90deg, 
                    ${theme.palette.primary.main}, 
                    ${theme.palette.secondary.main})`,
                },
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: `0 25px 50px -12px rgba(0,0,0,0.4), 
                             0 12px 24px -8px rgba(0,0,0,0.3)`,
                  bgcolor: "rgba(255, 255, 255, 0.98)",
                }
              }}
            >
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6, duration: 0.4 }}
                style={{ width: "100%" }}
              >
                <Outlet />
              </motion.div>
            </Paper>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8, duration: 0.4 }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "rgba(255, 255, 255, 0.7)",
                textAlign: "center",
                fontWeight: 500,
                letterSpacing: "0.5px",
                textShadow: "0 2px 4px rgba(0,0,0,0.3)"
              }}
            >
              Copyright © 2025 FreeTech Inc. All rights reserved.
            </Typography>
          </motion.div>
        </Box>
      </Container>
    </Box>
  );
};

export default AuthLayout;
